import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import { parsePrice, centsToDollars } from "helpers/price";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useContext, useState } from "react";
import { useCancelSingleOrderMutation } from "store/queries/order";
import styled from "styled-components";

const CancelOrderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: 450px;
`;

export function CancelOrderDialog({
  showCancelDialog,
  setShowCancelDialog,
  cancelNotes,
  setCancelNotes,
  orderId,
  onUpdateOrder,
  cancelOrderRefundAmount,
  cardLastDigits,
  isLoading,
  setIsAuthorizationDone,
}: {
  showCancelDialog: boolean;
  setShowCancelDialog: any;
  cancelNotes: string;
  setCancelNotes: any;
  orderId: string;
  onUpdateOrder: any;
  cancelOrderRefundAmount: number;
  cardLastDigits: string;
  isLoading: boolean;
  setIsAuthorizationDone: any;
}) {
  const [cancelSingleOrder] = useCancelSingleOrderMutation();
  const { current: toastElement } = useContext(ToastContext);

  const [loading, setLoading] = useState(false);

  const cancelOrder = () => {
    setLoading(true);

    cancelSingleOrder({
      orderId,
      note: cancelNotes.trim()?.length ? cancelNotes.trim() : undefined,
    })
      .unwrap()
      .then(async () => {
        toastElement?.show({
          severity: "success",
          summary: "Order canceled",
          detail: "Order canceled successfully.",
        });
        onUpdateOrder();
      })
      .catch((error) => {
        const apiMessage =
          Array.isArray(error?.data?.messages) && error.data.messages[0];
        const isConflict = error?.status === 409;

        toastElement?.show({
          severity: isConflict ? "warn" : "error",
          summary: isConflict ? "Unable to cancel" : "Error",
          detail: apiMessage || error.message,
          life: isConflict ? 8000 : 5000,
        });
      })
      .finally(() => {
        setLoading(false);
        setShowCancelDialog(false);
      });
  };

  const footerComponent = () => {
    return (
      <div>
        <Button
          label="No"
          onClick={() => {
            setCancelNotes("");
            setShowCancelDialog(false);
          }}
          className="p-button-link mr-2"
          disabled={loading}
        />
        <Button label="Cancel" onClick={cancelOrder} loading={loading} />
      </div>
    );
  };

  return (
    <Dialog
      onHide={() => {
        setCancelNotes("");
        setShowCancelDialog(false);
        setIsAuthorizationDone(false);
      }}
      visible={showCancelDialog}
      header="Confirmation"
      footer={footerComponent}
      closable={false}
    >
      <div className="flex gap-2">
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "28px" }}
        />
        {isLoading ? (
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        ) : (
          <CancelOrderContainer>
            <span>Are you sure you want to cancel this order?</span>
            {cancelOrderRefundAmount > 0 && (
              <span className="mt-1">
                {`This will refund
            ${parsePrice(centsToDollars(cancelOrderRefundAmount))} to patient's
            card ending ${cardLastDigits}`}
              </span>
            )}
            <InputTextarea
              value={cancelNotes}
              onChange={(e) => setCancelNotes(e.target.value)}
              rows={2}
              placeholder="Notes"
              className="w-full mt-3"
            />
          </CancelOrderContainer>
        )}
      </div>
    </Dialog>
  );
}
